import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  LockOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { history, useModel, useIntl } from "@umijs/max";
import type { MenuProps } from "antd";
import { Spin } from "antd";
import { createStyles } from "antd-style";
import React, { useState } from "react";
import { flushSync } from "react-dom";
import { useUserStore } from "@/stores/useUserStore";
// import { outLogin } from "@/services";
import HeaderDropdown from "../HeaderDropdown";
import { ChangePasswordModal } from "./ChangePasswordModal";
import PersonalInfoModal from "./PersonalInfoModal";
import AgentLevelTag from "../AgentLevelTag";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { userInfo: currentUser } = useUserStore();
  const name = currentUser?.nickName || currentUser?.userName;
  return <span className="anticon">{name}</span>;
};

export const AgentLevelTagWrapper = () => {
  const { userInfo: currentUser } = useUserStore();
  return <AgentLevelTag level={currentUser?.agentLevel} />;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: "flex",
      height: "48px",
      marginLeft: "auto",
      overflow: "hidden",
      alignItems: "center",
      padding: "0 8px",
      cursor: "pointer",
      borderRadius: token.borderRadius,
      "&:hover": {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    // await outLogin();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get("redirect");
    // Note: There may be security issues, please note
    if (window.location.pathname !== "/login" && !redirect) {
      history.replace({
        pathname: "/login",
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();
  const intl = useIntl();
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [personalInfoModalOpen, setPersonalInfoModalOpen] = useState(false);

  const { initialState, setInitialState } = useModel("@@initialState");
  const {
    userInfo: currentUser,
    loading: userStoreLoading,
    logout,
  } = useUserStore();

  const onMenuClick: MenuProps["onClick"] = (event) => {
    const { key } = event;
    if (key === "logout") {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      logout();
      loginOut();
      return;
    }
    if (key === "changePassword") {
      setChangePasswordModalOpen(true);
      return;
    }
    if (key === "personalInfo") {
      setPersonalInfoModalOpen(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loadingView = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (userStoreLoading || !currentUser) {
    return loadingView;
  }

  const name = currentUser?.nickName || currentUser?.userName;

  if (!name) {
    return loadingView;
  }

  const isPlatform = currentUser?.agentLevel === 1;

  const menuItems = [
    ...(!isPlatform
      ? [
          {
            key: "personalInfo",
            icon: <IdcardOutlined />,
            label: intl.formatMessage({ id: "menu.account.personal_info" }),
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    ...(menu
      ? [
          {
            key: "center",
            icon: <UserOutlined />,
            label: intl.formatMessage({ id: "menu.account.center" }),
          },
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: intl.formatMessage({ id: "menu.account.settings" }),
          },
          {
            key: "changePassword",
            icon: <LockOutlined />,
            label: intl.formatMessage({ id: "menu.account.change_password" }),
          },
          {
            type: "divider" as const,
          },
        ]
      : [
          {
            key: "changePassword",
            icon: <LockOutlined />,
            label: intl.formatMessage({ id: "menu.account.change_password" }),
          },
          {
            type: "divider" as const,
          },
        ]),
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: "menu.account.logout" }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <ChangePasswordModal
        open={changePasswordModalOpen}
        onOpenChange={setChangePasswordModalOpen}
      />
      <PersonalInfoModal
        open={personalInfoModalOpen}
        onOpenChange={setPersonalInfoModalOpen}
      />
    </>
  );
};
